import { useEffect, useRef, type FC } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import IncidentRosterData from 'components/IncidentConfig/IncidentRosterData';
import { registerHwcgTourApi } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import {
  HWCG_TOUR_PARAM,
  hwcgTourTasksHref,
  wantsHwcgTour,
} from 'components/IncidentConfig/hwcgTour/hwcgTourSteps';
import { useHwcgFlag, useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { registerFocPlanningTourApi } from 'components/MapPage/focPlanningTour/focPlanningTourBridge';
import {
  FOC_PLANNING_TOUR_PARAM,
  wantsFocPlanningTour,
} from 'components/MapPage/focPlanningTour/focPlanningTourSteps';
import { FOC_INCIDENT_ROSTER_TOUR_PARAM } from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourSteps';
import { FOC_EXT_TOUR_PARAM } from 'components/MapPage/focExtTour/focExtTourSteps';

const IAPRoster: FC = () => {
  const hwcg = useHwcgFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  const { incidentId } = useParams<{ incidentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const tourStartedRef = useRef(false);
  const focTourStartedRef = useRef(false);
  const focIncidentRosterStartedRef = useRef(false);
  const focExtTourStartedRef = useRef(false);
  const wantsTour =
    hwcg &&
    !!incidentId &&
    wantsHwcgTour(searchParams.get(HWCG_TOUR_PARAM), 'roster');
  const wantsFocTour =
    uscgFoc &&
    !!incidentId &&
    wantsFocPlanningTour(searchParams.get(FOC_PLANNING_TOUR_PARAM), 'roster');
  const wantsFocIncidentRosterTour =
    uscgFoc &&
    !!incidentId &&
    searchParams.get(FOC_INCIDENT_ROSTER_TOUR_PARAM) === '1';
  const wantsFocExtTour =
    uscgFoc && !!incidentId && searchParams.get(FOC_EXT_TOUR_PARAM) === '1';

  useEffect(() => {
    if (!incidentId) return;
    return registerHwcgTourApi({
      navigateTo: (href) => navigateRef.current(href),
      resolveTasksHref: () => hwcgTourTasksHref(incidentId),
    });
  }, [incidentId]);

  useEffect(() => {
    if (!wantsTour || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(HWCG_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/IncidentConfig/hwcgTour/hwcgTour')
        .then(({ startHwcgTour }) => {
          startHwcgTour('roster', true);
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsTour, setSearchParams]);

  useEffect(() => {
    return registerFocPlanningTourApi({
      navigateTo: (href) => navigateRef.current(href),
    });
  }, []);

  useEffect(() => {
    if (!wantsFocTour) {
      focTourStartedRef.current = false;
      return;
    }
    if (focTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focTourStartedRef.current) return;
      focTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_PLANNING_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focPlanningTour/focPlanningTour')
        .then(({ startFocPlanningTour }) => {
          startFocPlanningTour('roster');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocTour, setSearchParams]);

  useEffect(() => {
    if (!wantsFocIncidentRosterTour || focIncidentRosterStartedRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (focIncidentRosterStartedRef.current) return;
      focIncidentRosterStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_INCIDENT_ROSTER_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/MapPage/focIncidentRosterTour/focIncidentRosterTour'
      )
        .then(
          ({
            consumeFocIncidentRosterTourResume,
            startFocIncidentRosterTour,
          }) => {
            const resume = consumeFocIncidentRosterTourResume();
            startFocIncidentRosterTour({
              startId: resume ?? 'sectionIntro',
            });
          }
        )
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocIncidentRosterTour, setSearchParams]);

  useEffect(() => {
    if (!wantsFocExtTour || focExtTourStartedRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (focExtTourStartedRef.current) return;
      focExtTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_EXT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focExtTour/focExtTour')
        .then(({ startFocExtTour }) => startFocExtTour())
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocExtTour, setSearchParams]);

  // Only clear the start guard on unmount so stripping the query param cannot
  // re-arm a second auto-start while the driver overlay is still alive.
  useEffect(() => {
    return () => {
      focIncidentRosterStartedRef.current = false;
      focExtTourStartedRef.current = false;
    };
  }, []);

  return (
    <div className="maincontent">
      <div className="p-2">
        <IncidentRosterData />
      </div>
    </div>
  );
};

export default IAPRoster;
